import { fundingHqFetch } from './fundingApi';

function agencyQuery(agencyId) {
  return new URLSearchParams({ agency_id: agencyId }).toString();
}

export function isFundingApplicationDebugEnabled() {
  return process.env.NODE_ENV !== 'production'
    && process.env.REACT_APP_FUNDING_DEBUG === 'true';
}

function logApplicationDebug(message, meta = {}) {
  if (!isFundingApplicationDebugEnabled()) return;
  console.info(`[FundingApplications] ${message}`, meta);
}

function assertClientId(clientId) {
  const scopedId = String(clientId ?? '').trim();
  if (!scopedId) {
    throw new Error('Client ID is required for application requests.');
  }
  return scopedId;
}

function clientApplicationsPath(clientId, agencyId) {
  return `/clients/${encodeURIComponent(assertClientId(clientId))}/applications?${agencyQuery(agencyId)}`;
}

function clientMetricsPath(clientId, agencyId) {
  return `/clients/${encodeURIComponent(assertClientId(clientId))}/application-metrics?${agencyQuery(agencyId)}`;
}

export async function fetchClientApplications(clientId, agencyId) {
  const path = clientApplicationsPath(clientId, agencyId);
  logApplicationDebug('applications request', {
    clientId,
    agencyId,
    url: path,
  });
  const data = await fundingHqFetch(path);
  const applications = data.applications ?? [];
  logApplicationDebug('applications response', {
    clientId,
    agencyId,
    count: applications.length,
  });
  return applications;
}

export async function fetchApplicationMetrics(clientId, agencyId) {
  const path = clientMetricsPath(clientId, agencyId);
  logApplicationDebug('metrics request', {
    clientId,
    agencyId,
    url: path,
  });
  const metrics = await fundingHqFetch(path);
  logApplicationDebug('metrics response', {
    clientId,
    agencyId,
    totalApplications: metrics?.totalApplications ?? 0,
  });
  return metrics;
}

export async function createClientApplication(clientId, agencyId, payload) {
  return fundingHqFetch(
    clientApplicationsPath(clientId, agencyId),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export async function updateClientApplication(clientId, agencyId, applicationId, payload) {
  return fundingHqFetch(
    `/clients/${encodeURIComponent(clientId)}/applications/${encodeURIComponent(applicationId)}?${agencyQuery(agencyId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteClientApplication(clientId, agencyId, applicationId) {
  return fundingHqFetch(
    `/clients/${encodeURIComponent(clientId)}/applications/${encodeURIComponent(applicationId)}?${agencyQuery(agencyId)}`,
    { method: 'DELETE' },
  );
}

function trimOrNull(value) {
  const cleaned = String(value ?? '').trim();
  return cleaned || null;
}

function numberOrNull(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function isApplicationCoreComplete(app = {}) {
  return Boolean(
    trimOrNull(app.dateOfApplication)
    && trimOrNull(app.lender)
    && trimOrNull(app.lendingProduct)
    && trimOrNull(app.decision),
  );
}

function mapApplicantsForApi(applicants = []) {
  return applicants.map((applicant) => ({
    owner_id: applicant.ownerId || null,
    role_on_application: applicant.roleOnApplication || null,
    ownership_percent_used: applicant.ownershipPercentUsed ?? null,
  }));
}

/** Map API camelCase application to snake_case payload for PATCH/POST */
export function applicationToPayload(app) {
  const coreComplete = isApplicationCoreComplete(app);
  return {
    identity_profile_id: app.identityProfileId || null,
    entity_id: app.entityId || null,
    applicants: mapApplicantsForApi(app.applicants || []),
    date_of_application: trimOrNull(app.dateOfApplication),
    lender: trimOrNull(app.lender),
    lending_product: trimOrNull(app.lendingProduct),
    application_type: trimOrNull(app.applicationType),
    personal_or_business: trimOrNull(app.personalOrBusiness),
    zero_percent_offer: app.zeroPercentOffer === 'Yes' ? true : app.zeroPercentOffer === 'No' ? false : null,
    approval_amount: numberOrNull(app.approvalAmount),
    decision: trimOrNull(app.decision),
    zero_percent_term: trimOrNull(app.zeroPercentTerm),
    owner_name: trimOrNull(app.ownerName),
    ssn: trimOrNull(app.ssn),
    dob: trimOrNull(app.dob),
    full_ein: trimOrNull(app.fullEin),
    business_start_date: trimOrNull(app.businessStartDate),
    employer_name: trimOrNull(app.employerName),
    time_live_in_house_years: app.timeLiveInHouseYears ?? null,
    time_live_in_house_months: app.timeLiveInHouseMonths ?? null,
    time_working_at_years: app.timeWorkingAtYears ?? null,
    time_working_at_months: app.timeWorkingAtMonths ?? null,
    experian_score_used: app.experianScoreUsed ?? null,
    transunion_score_used: app.transunionScoreUsed ?? null,
    equifax_score_used: app.equifaxScoreUsed ?? null,
    personal_income: numberOrNull(app.personalIncome),
    business_income: numberOrNull(app.businessIncome),
    housing: numberOrNull(app.housing),
    business_expenses: numberOrNull(app.businessExpenses),
    savings: numberOrNull(app.savings),
    retirement_account: numberOrNull(app.retirementAccount),
    personal_address: trimOrNull(app.personalAddress),
    business_address: trimOrNull(app.businessAddress),
    notes: trimOrNull(app.notes),
    status: coreComplete ? trimOrNull(app.status) : 'Draft',
    source: trimOrNull(app.source),
    selected_bureau_pull: trimOrNull(app.selectedBureauPull),
  };
}
